import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthNavbar from '../common/AuthNavbar';
import {
  activateSubscriptionRemote,
  consumeCheckoutTenantId,
  getOrganizationById,
  getTenantId,
  peekCheckoutTenantId,
  PRODUCT_NAME,
} from '../../lib/tenant';

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const tenantFromQuery = searchParams.get('tenant')?.trim();
  const tenantId =
    tenantFromQuery || peekCheckoutTenantId() || getTenantId() || '';

  const planId = (searchParams.get('plan')?.trim() || 'premium').toLowerCase();
  const org = tenantId ? getOrganizationById(tenantId) : undefined;

  const completeDemoPayment = async () => {
    if (!tenantId || !org) {
      setMsg('Missing or invalid organization. Start from register or pricing.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await activateSubscriptionRemote(tenantId, planId);
      consumeCheckoutTenantId();
      const logged = localStorage.getItem('isLoggedIn') === 'true';
      if (logged) {
        navigate('/dashboard');
      } else {
        navigate('/login', { state: { checkoutOk: true } });
      }
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Could not complete checkout.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-zinc-100 font-sans">
      <AuthNavbar />
      <main className="mx-auto max-w-lg px-4 pb-24 pt-24">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-800">{PRODUCT_NAME}</p>
        <h1 className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">Checkout</h1>
        <p className="mt-2 text-sm text-zinc-600">
          Demo mode: no real card is charged. In production, embed Stripe Checkout or your PSP and activate the
          subscription on your server when payment succeeds.
        </p>

        <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
          {!org ? (
            <p className="text-sm text-zinc-700">
              We could not load this checkout session.{' '}
              <button type="button" className="font-semibold text-emerald-700 hover:underline" onClick={() => navigate('/register')}>
                Register an organization
              </button>{' '}
              or return to{' '}
              <button type="button" className="font-semibold text-emerald-700 hover:underline" onClick={() => navigate('/pricing')}>
                pricing
              </button>
              .
            </p>
          ) : (
            <>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-zinc-500">Organization</dt>
                  <dd className="font-medium text-zinc-900">{org.name}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-zinc-500">Plan</dt>
                  <dd className="font-medium text-zinc-900 capitalize">{planId}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-zinc-500">Status</dt>
                  <dd className="font-medium text-zinc-900">{org.subscriptionStatus.replace('_', ' ')}</dd>
                </div>
              </dl>

              {msg && <p className="mt-4 text-sm text-red-700">{msg}</p>}

              <button
                type="button"
                disabled={busy || org.subscriptionStatus === 'active'}
                onClick={completeDemoPayment}
                className="mt-6 w-full rounded-xl bg-gradient-to-b from-emerald-600 to-emerald-800 py-3 text-sm font-semibold text-white shadow-md hover:from-emerald-500 hover:to-emerald-700 disabled:opacity-50"
              >
                {org.subscriptionStatus === 'active'
                  ? 'Already active'
                  : busy
                    ? 'Processing…'
                    : 'Complete payment (demo)'}
              </button>

              {org.subscriptionStatus === 'active' && (
                <button
                  type="button"
                  className="mt-3 w-full rounded-xl border border-zinc-200 py-3 text-sm font-semibold text-zinc-800 hover:bg-zinc-50"
                  onClick={() => navigate('/login')}
                >
                  Go to sign in
                </button>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
